/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { JSX } from 'preact';
import type { ForwardedRef } from 'preact/compat';
import type { CurrentItemVariant } from '#UNSAFE_Collection';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type { Cell, CellOverride, ListHandle, ListViewportConfig, Props } from './List.types';
import type { ListVariantOptions } from './themes/ListStyles.css';

import { createRef } from 'preact';
import { forwardRef } from 'preact/compat';
import { useCallback, useContext, useState } from 'preact/hooks';
import { useCollectionInteractionContext } from '#hooks/UNSAFE_useCollectionInteractionContext';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useId } from '#hooks/UNSAFE_useId';
import { TabbableModeContext } from '#hooks/UNSAFE_useTabbableMode';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import { useUser } from '#hooks/UNSAFE_useUser';
import { classNames } from '#utils/UNSAFE_classNames';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { FocusTracker } from '#PRIVATE_FocusTracker';
import { useDataManager } from '#PRIVATE_TableCommon/hooks/useDataManager';
import { useInteractionManager } from './hooks/useInteractionManager';
import { useSizingManager } from './hooks/useSizingManager';
import { scrollerStyles } from './themes/List.css';
import { ListRedwoodTheme } from './themes/redwood/ListTheme';
import { ListBody } from './ListBody';
import { ListContextMenu } from './ListContextMenu';

export type ActiveCell<K> =
  | Cell<K>
  | {
      rowKey?: never;
      type: 'loading';
    }
  | {
      rowKey?: never;
      type: 'loadMore';
    };

export type ActiveCellDetail<K> = {
  value?: ActiveCell<K>;
};

export type CurrencyStatus<K, D> = {
  activeCell?: ActiveCell<K>;
  currentCell?: Cell<K>;
  currentItemOverride?: CellOverride<K>;
  hasPendingCurrentCellOverride: boolean;
  hasPendingCurrentCellOverrideScroll: boolean;
  previousEmbeddedSelectCurrentCell?: Cell<K>;
  loadMoreInitialData?: CollectionData<K, D>[];
};

export type ViewportInfo = {
  isExternalScroller: boolean;
  viewportConfig: ListViewportConfig;
};

export type ScrollDetail<K> = {
  cell: ActiveCell<K>;
  locationX?: 'inView' | 'start' | 'end';
  locationY?: 'inView' | 'top' | 'bottom';
};

const _defaultSelectedKeys = { all: false, keys: new Set() };

/**
 * Component that renders data in a flat list.
 */
export const List = forwardRef(function List<K extends string | number, D>(
  {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    children = () => undefined,
    contextMenuConfig,
    viewportConfig,
    data,
    getRowKey,
    hasMore = false,
    onLoadMore = () => {},
    currentItemOverride,
    onPersistCurrentItem,
    currentItemVariant,
    itemPadding = 'disabled',
    itemEnterKeyFocusBehavior = 'focusWithin',
    gridlines = { item: 'hidden', top: 'hidden', bottom: 'hidden' },
    selectionMode,
    selectedKeys = _defaultSelectedKeys as NonNullable<Props<K, D>['selectedKeys']>,
    onSelectionChange,
    onItemAction,
    scrollPositionOverride,
    promotedSection,
    skeletonRenderer,
    noDataRenderer,
    scrollerRef,
    onReorder,
    testId
  }: Props<K, D>,
  ref?: ForwardedRef<ListHandle<K>>
) {
  const listId = useId();
  const testIdProps = useTestId(testId);

  // rootRef is callback-based to meet useResizeObserver hook's state management requirements
  const [rootRef, setRootRef] = useState(createRef<HTMLDivElement>());
  const rootRefCallback = useCallback((node: HTMLDivElement | null) => {
    setRootRef({ current: node });
  }, []);

  const { direction, forcedColors } = useUser();
  const isHighContrast = forcedColors === 'active';
  const isRtl = direction === 'rtl';

  const isEmbedded = useCollectionInteractionContext() === 'embedded';
  const itemVariant: CurrentItemVariant = currentItemVariant ?? isEmbedded ? 'highlight' : 'none';

  const { managedData: listData, onManagedLoadMore: onListLoadMore } = useDataManager<K, D>({
    data,
    getRowKey,
    hasMore,
    onLoadMore
  });

  const { viewportInfo, scrollerRefCallback } = useSizingManager({
    viewportConfig,
    externalScrollerAccessRef: scrollerRef
  });

  const ariaRowCount = hasMore ? -1 : listData.length;

  const {
    interactionManagerProps,
    scrollHandlingProps,
    onListSelectionChange,
    isShowFocusRing,
    isTabbableMode,
    currentCell,
    currentActiveCell,
    onStartFocusTracking,
    onFocusStartEdge,
    onFocusEndEdge,
    contextMenuContext,
    menuProps,
    disabledRowInteractionKeys,
    reorderItemProps,
    reorderInstructions,
    reorderInstructionsId
  } = useInteractionManager<K, D>({
    rootRef,
    viewportInfo,
    ref,
    listId,
    listData,
    hasMore,
    isEmbedded,
    isLayoutRequired: false,
    scrollPositionOverride,
    currentItemOverride,
    onPersistCurrentItem,
    selectionMode,
    selectedKeys,
    onSelectionChange,
    onItemAction,
    itemEnterKeyFocusBehavior,
    contextMenuConfig,
    onReorder
  });

  // initialize base List style classes
  const { classes: themeClasses } = useComponentTheme<ListVariantOptions>(ListRedwoodTheme, {
    showFocusRing: isShowFocusRing ? 'isShowFocusRing' : 'notShowFocusRing',
    externalScroller: viewportInfo.isExternalScroller ? 'isExternalScroller' : 'notExternalScroller'
  });

  const scrollerClassArray = [scrollerStyles.base];
  if (viewportInfo.isExternalScroller) {
    scrollerClassArray.push(scrollerStyles.externalScroller);
  } else {
    scrollerClassArray.push(scrollerStyles.autoScroll);
  }
  const scrollerClasses = classNames(scrollerClassArray);
  const classes = classNames([themeClasses]);

  const hasItemGridlines = gridlines?.item === 'visible';
  const hasTopGridline = gridlines?.top === 'visible';
  const hasBottomGridline = gridlines?.bottom === 'visible';

  const isSelectionEnabled =
    selectionMode === 'single' ||
    selectionMode === 'multiple' ||
    selectionMode === 'multipleToggle';
  const supportsMultipleSelection =
    selectionMode === 'multiple' || selectionMode === 'multipleToggle';

  // honor outer isTabbable context for embedded scenarios
  const { isTabbable } = useContext(TabbableModeContext);

  const getListBodyProps = () => {
    return {
      viewportInfo,
      listId,
      listData,
      children,
      itemPadding,
      hasItemGridlines,
      hasTopGridline,
      hasBottomGridline,
      selectedKeys: selectedKeys,
      isSelectionEnabled,
      isSingleSelectionEnabled: selectionMode === 'single',
      activeCell: currentActiveCell,
      currentItemKey: currentCell?.type === 'data' ? currentCell.rowKey : undefined,
      currentItemVariant: itemVariant,
      isShowFocusRing,
      isTabbableMode: isTabbable && isTabbableMode,
      hasMore,
      onLoadMore: onListLoadMore,
      promotedSection,
      noDataRenderer,
      skeletonRenderer,
      disabledInteractionKeys: disabledRowInteractionKeys,
      onSelectionChange: onListSelectionChange,
      isRtl,
      isHighContrast,
      reorderItemProps
    };
  };

  const isReorderEnabled = onReorder != null;
  const compositeDescription =
    ariaDescribedBy != null
      ? isReorderEnabled
        ? `${ariaDescribedBy} ${reorderInstructionsId}`
        : ariaDescribedBy
      : isReorderEnabled
      ? reorderInstructionsId
      : undefined;

  const ariaMultiSelectableProps = supportsMultipleSelection
    ? { 'aria-multiselectable': true }
    : isSelectionEnabled ? { 'aria-multiselectable' : false } : {};

  return (
    <>
      <div
        {...testIdProps}
        ref={rootRefCallback}
        aria-colcount={1}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={compositeDescription}
        aria-rowcount={ariaRowCount}
        {...ariaMultiSelectableProps}
        class={classes}
        id={listId}
        role={'grid'}
        tabIndex={currentActiveCell != null || !isTabbable ? -1 : 0}
        {...interactionManagerProps}>
        <div
          ref={scrollerRefCallback}
          class={scrollerClasses}
          role={'rowgroup'}
          tabIndex={-1}
          {...scrollHandlingProps}>
          <FocusTracker
            isDisabled={!isTabbableMode}
            onFocusStartEdge={onFocusStartEdge}
            onFocusEndEdge={onFocusEndEdge}
            onStartTracking={onStartFocusTracking}>
            <TabbableModeContext.Provider value={{ isTabbable: isTabbableMode }}>
              <ListBody {...getListBodyProps()} />
            </TabbableModeContext.Provider>
          </FocusTracker>
        </div>
      </div>
      {isReorderEnabled && (
        <span id={reorderInstructionsId}>
          <HiddenAccessible>{reorderInstructions}</HiddenAccessible>
        </span>
      )}
      {contextMenuConfig != null && contextMenuContext != null && (
        <ListContextMenu
          {...menuProps}
          contextMenuConfig={contextMenuConfig}
          contextMenuContext={contextMenuContext}
        />
      )}
    </>
  );
}) as <K extends string | number, D>(
  p: Props<K, D> & { ref?: ForwardedRef<ListHandle<K>> }
) => JSX.Element;
